/**
 * @file ScanField.tsx
 * @description Поле «скан / поиск» экрана кладовщика (phase19.14; режим
 * поиска — phase19.23; переезд в `components/ui` — phase19.24).
 *
 * Домен-нейтральный блок UI-контракта: одно поле на приёмку, списание,
 * перемещение и сборку (старый путь `picking/ScanInputField` — реэкспорт).
 *
 * USB-сканер работает как клавиатура: символы приходят пачкой и завершаются
 * Enter — отдельного режима «сканер» нет и быть не должно (память проекта
 * `feedback_warehouse_ui_mobile_first`), поэтому одно поле закрывает и скан, и
 * ручной ввод, и поиск — как `ScanSearchField` в мобильном приложении.
 *
 * Два режима: `scanOnly` (поле чистится после Enter — серия сканов подряд) и
 * `scanAndSearch` (текст остаётся и уходит в `onQueryChange`). Звук и вибро —
 * настройка устройства («Настройки склада → Рабочее место» и «⋯» документа),
 * здесь только значок.
 *
 * Фокус — главное в этом компоненте. Поле НЕ дизейблится на время запроса:
 * сканер продолжает «печатать» в него, а браузер снял бы фокус с
 * disabled-input, и следующий скан улетел бы в document. Гасим только
 * отправку: Enter во время запроса игнорируем, вместо иконки крутим индикатор,
 * а по окончании запроса возвращаем фокус (`ScanFieldHandle.focus`).
 *
 * Камера (phase19.22 шаг 5): на телефоне в поле есть «Сканировать камерой»
 * (`CameraScanButton`) — код уходит в `onSubmit` с `fromScanner: true`;
 * клавиатуру после этого на телефоне не поднимаем (`useScanFieldFocus`).
 *
 * phase19.31 (зона «Склад», замечание владельца «поля Скан не вписываются»,
 * SC43/SC44): поле выглядит как обычное поле ввода кабинета — высота из темы
 * (48 до 1024, 44 с 1024, 56 только у кладовщика до 1024 через плотность),
 * та же рамка, скругление 8px и шрифт поля; значок скана — внутри слева
 * вторым цветом, кнопки «очистить» и «камера» — компактные значки того же
 * ряда. Своих крупных высот больше нет: `density` оставлен для совместимости.
 */

import { Loader2, ScanLine, VolumeX, X } from "lucide-react";
import { useImperativeHandle, useRef, useState } from "react";

import { CameraScanButton } from "./CameraScanButton";
import type { CameraMode } from "./camera-scan";
import { useScanFeedbackSetting } from "./useScanFeedbackSetting";
import { useScanFieldFocus } from "./useScanFieldFocus";
import { useScanPace } from "./useScanPace";
import { cn } from "@/lib/utils";

/** Императивный доступ: фокус после ±, диалога, обновления списка. */
export interface ScanFieldHandle {
  focus: () => void;
  /** Очистить поле и сбросить фильтр (экран отметил позицию, «Сбросить»). */
  clear: () => void;
}

/** Плейсхолдер — один на все документы (решение пользователя 2026-09-20). */
export const WMS_SCAN_SEARCH_HINT = "Артикул или скан";
export const WMS_SCAN_ONLY_HINT = "Артикул или скан";

interface Props {
  /** Введённый код (по Enter) и источник: скан или руки. */
  onSubmit: (code: string, meta: { fromScanner: boolean }) => void;
  /** `scanAndSearch` — текст остаётся в поле и уходит в `onQueryChange`. */
  mode?: "scanOnly" | "scanAndSearch";
  /** Живой ввод фильтра — только в режиме `scanAndSearch`. */
  onQueryChange?: (value: string) => void;
  /** Подсказка под полем: что даст ввод (мобильный показывает так же). */
  helperText?: string;
  /** true — идёт запрос: отправку игнорируем, ввод и фокус не трогаем. */
  busy?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** false — прячет значок «звук выключен»: в поиске реестра он не про скан. */
  showFeedbackIndicator?: boolean;
  /** false — не брать фокус при монтировании (второе поле формы: ячейка). */
  autoFocus?: boolean;
  /** Кнопка камеры: `auto` — на сенсорных устройствах с камерой. */
  camera?: CameraMode;
  /**
   * @deprecated phase19.31: высоту даёт тема (стандарт; 56 — у кладовщика до
   * 1024 через плотность каркаса). Значение не влияет на вид.
   */
  density?: "wms" | "standard";
  ref?: React.Ref<ScanFieldHandle>;
}

/** Рамка поля — как у поля ввода темы: высота, скругление, шрифт ввода. */
const FIELD_CLASS = {
  box: cn(
    "min-h-[var(--control-h,3rem)] rounded-lg bg-[var(--input,var(--surface-lowest))]",
    "[box-shadow:inset_0_0_0_1px_var(--field-border,var(--border))]",
    "hover:[box-shadow:inset_0_0_0_1px_var(--field-border-hover,var(--border))]",
    "focus-within:[box-shadow:inset_0_0_0_1px_var(--field-border-focus,var(--primary)),0_0_0_2px_var(--ring)]",
  ),
  input: "text-[length:var(--fs-field,1rem)]",
  clear: "size-[var(--control-compact-h,3rem)]",
} as const;

export function ScanField({
  onSubmit,
  mode = "scanOnly",
  onQueryChange,
  helperText,
  busy = false,
  disabled = false,
  placeholder,
  showFeedbackIndicator = true,
  autoFocus = true,
  camera = "auto",
  ref,
}: Props) {
  const sizes = FIELD_CLASS;
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const pace = useScanPace();
  // Индикатор «звук выключен» (тумблер — в настройках устройства).
  const scanFeedback = useScanFeedbackSetting();
  const searchable = mode === "scanAndSearch";
  const hint =
    placeholder ?? (searchable ? WMS_SCAN_SEARCH_HINT : WMS_SCAN_ONLY_HINT);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    clear: () => clearField(),
  }));

  // Автофокус, возврат фокуса после запроса и правило камеры — в хуке.
  const focus = useScanFieldFocus({ inputRef, busy, disabled, autoFocus });

  function changeValue(next: string) {
    focus.release();
    pace.track(next, value);
    setValue(next);
    if (searchable) onQueryChange?.(next);
  }

  function clearField() {
    pace.reset();
    setValue("");
    if (searchable) onQueryChange?.("");
    if (!disabled) inputRef.current?.focus();
  }

  function submit() {
    // Со сканера — только последняя «пачка»: ручной текст фильтра не отправляем.
    const { burst, fromScanner } = pace.read(value);
    const code = (fromScanner ? burst : value).trim();
    // Идёт запрос — глотаем Enter, но введённое не стираем (дожмут ещё раз).
    if (!code || busy || disabled) return;
    if (!searchable) {
      pace.reset();
      setValue("");
    }
    onSubmit(code, { fromScanner });
    inputRef.current?.focus();
  }

  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          "flex items-center gap-2 pl-3 pr-1",
          sizes.box,
          disabled && "opacity-60",
        )}
      >
        {/* Значок поля скана — всегда «скан» (и в режиме «скан + поиск»):
            лупа — только у поля поиска `SearchInput` (дефект приёмки
            2026-10-02: в сборке стояла лупа, в приёмке — скан). */}
        {busy ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-primary" aria-label="Отправляем скан" />
        ) : (
          <ScanLine className="size-4 shrink-0 text-on-surface-variant" aria-hidden />
        )}
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          disabled={disabled}
          placeholder={hint}
          onChange={(e) => changeValue(e.target.value)}
          onPointerDown={focus.release}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            // Сканер шлёт Enter как обычную клавишу — форму сабмитить нечему,
            // но preventDefault страхует от закрытия родительских диалогов.
            e.preventDefault();
            submit();
          }}
          aria-label="Поле скана и поиска: артикул, штрихкод, название или адрес ячейки"
          aria-busy={busy}
          className={cn("min-w-0 flex-1 bg-transparent outline-none placeholder:text-on-surface-variant", sizes.input)}
        />
        {showFeedbackIndicator && !scanFeedback.enabled ? (
          <VolumeX className="size-4 shrink-0 text-on-surface-variant" aria-label="Звук скана выключен" />
        ) : null}
        {searchable && value ? (
          <button
            type="button"
            onClick={clearField}
            disabled={disabled}
            aria-label="Очистить поиск"
            title="Очистить"
            className={cn("inline-flex shrink-0 items-center justify-center rounded-md text-on-surface-variant hover:bg-surface-low", sizes.clear)}
          >
            <X className="size-4" />
          </button>
        ) : null}
        {/* Код с камеры — как скан USB; текст поля (фильтр) не трогаем. */}
        <CameraScanButton
          mode={camera}
          disabled={disabled || busy}
          onDetected={(code) => {
            onSubmit(code, { fromScanner: true });
            focus.afterCameraScan();
          }}
        />
      </div>
      {helperText ? <p className="type-caption text-on-surface-variant">{helperText}</p> : null}
    </div>
  );
}
